import { AppNS } from 'app';
import { ApiNS } from 'app/api/api';
import { request } from 'app/api_v2/config';
import axios, { Canceler } from 'axios';

import { API_URL } from '../../core/config';
import { getErrorMessage } from '../helpers/errorHandling';
import { normalizeToLead, parseProjectLead, preparePickedPermitsToAdd } from './client-bookings-parsers';

const URL = 'jobs';
const LEADS_URL = 'job_leads';
const BOOKINGS_URL = 'bookings';
const STATE_URL = 'change-state';
const REVIEWS_URL = 'reviews';
const JOB_CHANGES_URL = 'job-changes';

const CancelToken = axios.CancelToken;
let cancel: Canceler;

export function apiGetProjectLeadsForListing(
  type: string,
  params: AppNS.Params,
  signal?: AbortSignal
): Promise<AppNS.GetProjectLeadsApiResponse> {
  if (type === 'pending-matches') {
    return apiGetJobsForListing({ ...params, states: ['new'] }, signal);
  }

  if (type === 'confirmed-bookings') {
    return apiGetBookingsForListing({ ...params, states: ['confirmed'] }, signal);
  }

  return apiGetBookingsForListing(
    { ...params, states: ['past', 'cancelled_by_agency', 'cancelled_by_admin', 'cancelled_by_client'] },
    signal
  );
}

export function apiGetJobsForListing(
  params: AppNS.Params,
  signal?: AbortSignal
): Promise<AppNS.GetProjectLeadsApiResponse> {
  return request(
    {
      url: `${URL}`,
      method: 'GET',
      baseURL: API_URL,
      params: {
        ...params,
        per_page: params.perPage || 20,
      },
      signal,
    },
    (data) => {
      return {
        ...data,
        results: data.results.map((result: AppNS.Params) => {
          return { ...result, job: result };
        }),
      };
    }
  );
}

export function apiGetBookingsForListing(
  params: AppNS.Params,
  signal?: AbortSignal
): Promise<AppNS.GetProjectLeadsApiResponse> {
  return request(
    {
      url: `${BOOKINGS_URL}`,
      method: 'GET',
      baseURL: API_URL,
      params: {
        ...params,
        per_page: params.perPage || 20,
      },
      signal,
    },
    (data) => {
      return {
        ...data,
        results: data.results.map((item: AppNS.ProjectLead) => {
          return normalizeToLead(item);
        }),
      };
    }
  );
}

export function apiGetProjectLead(type: string, id: string): Promise<AppNS.ProjectLead> {
  if (cancel !== undefined) {
    cancel();
  }

  if (type === 'pending-matches' || type === 'job-opportunities') {
    return apiGetProjectLeadById(id);
  } else {
    return apiGetJobBookingById(id);
  }
}

export function apiUpdatePaymentMethod(reqData: AppNS.Params): Promise<AppNS.Params> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${BOOKINGS_URL}/update-payment-method.json`, reqData)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        return reject(error.response.data.errors);
      });
  });
}

export function apiGetProjectLeadById(leadId: string): Promise<AppNS.ProjectLead> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${LEADS_URL}/${leadId}.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(parseProjectLead(data.model));
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetJobBookingById(bookingId: string): Promise<AppNS.ProjectLead> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${BOOKINGS_URL}/${bookingId}.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(parseProjectLead(data.model));
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSetLeadState(reqData: AppNS.Params): Promise<AppNS.ProjectLeadState> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${LEADS_URL}/${STATE_URL}.json`, reqData)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.properties.state);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiCancelJob(reqData: AppNS.Params, requestOnly: boolean): Promise<string> {
  const data = {
    id: reqData.id,
    properties: {
      cancellationMessage: reqData.message || 'Cancelled by client',
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${URL}/${requestOnly ? 'request-cancellation.json' : 'cancel.json'}`, data)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiAddBookingReview(reqData: ApiNS.AddBookingReviewRequest): Promise<string> {
  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/${REVIEWS_URL}.json`, reqData)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiAddNewPermit(id: string, permit: AppNS.PickedPermit): Promise<string> {
  const req = {
    properties: {
      jobId: id,
      permits: preparePickedPermitsToAdd([permit]),
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/${JOB_CHANGES_URL}.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessage(error));
      });
  });
}

export function apiChangeClientProjectOwner(id: string, clientId: string): Promise<string> {
  const req = {
    id: id,
    properties: {
      clientId: clientId,
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/bookings/assign-client-pm.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessage(error));
      });
  });
}
export function apiChangeAgencyProjectOwner(id: string, agencyId: string): Promise<string> {
  const req = {
    id: id,
    properties: {
      agencyId: agencyId,
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/bookings/assign-agency-pm.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessage(error));
      });
  });
}

export function apiUpdateSiteContact(id: string, reqData: AppNS.Params): Promise<AppNS.Project> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/jobs/update-site-contact.json`, {
        id,
        properties: reqData,
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        return reject(error.response.data.errors);
      });
  });
}
